// ============================================
// Customer Funnel Personas — Shared Definitions
// 8 real customer types who land on hemologix.com,
// mapped from website visitor behavior and testimonials.
// Used by: TeachMode, TwinFighter, DocModule
// ============================================

import type { TestPersona, Touchpoint, ContextLayer } from '../../types/patientJourney';
import { DEFAULT_CONTEXT } from '../../types/patientJourney';

export function buildContextModifiers(overrides?: Partial<ContextLayer>): ContextLayer {
  return { ...DEFAULT_CONTEXT, ...overrides };
}

export function buildDefaultTouchpoint(personaId: string): Touchpoint {
  return {
    touchpointId: `tp-first-${personaId}`,
    type: 'first_contact',
    order: 1,
    label: 'First Contact - Lab Upload',
    trigger: 'Patient uploads lab results for the first time',
    conversationScript: [],
    validationChecklist: [],
  };
}

export const PERSONA_OPTIONS = [
  { value: 'gaslit', label: 'Elena Vasquez', sub: 'The Gaslit Patient' },
  { value: 'skeptic', label: 'Derek Sloan', sub: 'The Reluctant Skeptic' },
  { value: 'desperate', label: 'Priya Mehta', sub: 'The Desperate Searcher' },
  { value: 'biohacker', label: 'Tyler Okafor', sub: 'The Biohacker' },
  { value: 'anxious', label: 'Lily Tran', sub: 'The Anxious First-Timer' },
  { value: 'spouse', label: 'Diane Marshall', sub: 'The Spouse Who Cares' },
  { value: 'prepper', label: 'Greg Hoffman', sub: "The Doctor's Visit Prepper" },
  { value: 'price_watcher', label: 'Carmen Reyes', sub: 'The Price-Watcher' },
] as const;

export const PERSONA_MAP: Record<string, TestPersona> = {
  // ── 1. THE GASLIT PATIENT ──
  // "My doctor says I'm fine but I don't feel fine."
  gaslit: {
    personaId: 'teach-gaslit',
    name: 'Elena Vasquez',
    archetype: 'The Gaslit Patient',
    demographics: { name: 'Elena Vasquez', age: 43, sex: 'Female', state: 'CA' },
    labs: [
      { biomarker: 'TSH', value: 3.4, unit: 'mIU/L', conventionalRange: '0.45-4.5', functionalRange: '1.0-2.0', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Free T3', value: 2.2, unit: 'pg/mL', conventionalRange: '2.0-4.4', functionalRange: '3.0-4.0', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'TPO Antibodies', value: 48, unit: 'IU/mL', conventionalRange: '0-34', functionalRange: '0-15', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Ferritin', value: 18, unit: 'ng/mL', conventionalRange: '12-150', functionalRange: '50-100', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Vitamin D', value: 22, unit: 'ng/mL', conventionalRange: '20-50', functionalRange: '40-60', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [],
    conditions: ['Fatigue', 'Hair thinning', 'Afternoon energy crashes', 'Brain fog'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({
      motivation: 'concerned',
      baselineStress: 'high',
      healthKnowledge: 'basic',
      aiComfort: 'cautious',
      socialSupport: 'moderate',
    }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'gaslit-001', check: 'Nurse validates patient frustration before presenting clinical data', category: 'conversational', severity: 'critical', automatable: true },
      { checkId: 'gaslit-002', check: 'Nurse explains the difference between standard normal and functional optimal ranges', category: 'clinical', severity: 'major', automatable: true },
      { checkId: 'gaslit-003', check: 'Nurse connects lab findings to patient symptoms (fatigue, hair thinning → ferritin/thyroid)', category: 'clinical', severity: 'major', automatable: true },
      { checkId: 'gaslit-004', check: 'No dismissive language — never implies symptoms are imaginary or exaggerated', category: 'conversational', severity: 'critical', automatable: true },
      { checkId: 'gaslit-005', check: 'Nurse mentions specific biomarker values when explaining findings', category: 'clinical', severity: 'minor', automatable: true },
    ],
  },

  // ── 2. THE RELUCTANT SKEPTIC ──
  // "Another AI health thing, great."
  skeptic: {
    personaId: 'teach-skeptic',
    name: 'Derek Sloan',
    archetype: 'The Reluctant Skeptic',
    demographics: { name: 'Derek Sloan', age: 46, sex: 'Male', state: 'CO' },
    labs: [
      { biomarker: 'Total Testosterone', value: 340, unit: 'ng/dL', conventionalRange: '264-916', functionalRange: '500-900', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Free Testosterone', value: 6.8, unit: 'ng/dL', conventionalRange: '5.0-21.0', functionalRange: '10-20', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'DHEA-S', value: 140, unit: '\u00b5g/dL', conventionalRange: '80-560', functionalRange: '200-400', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'hs-CRP', value: 2.8, unit: 'mg/L', conventionalRange: '0-3.0', functionalRange: '0-1.0', status: 'above_functional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [],
    conditions: ['Low energy', 'Brain fog', 'Poor recovery from workouts'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({
      motivation: 'skeptical',
      baselineStress: 'low',
      healthKnowledge: 'informed',
      aiComfort: 'distrustful',
      budgetSensitivity: 'moderate',
      supplementExperience: 'some',
    }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'skeptic-001', check: 'Nurse leads with specific data and numbers, not emotional language', category: 'conversational', severity: 'critical', automatable: true },
      { checkId: 'skeptic-002', check: 'Nurse does not oversell or make exaggerated claims about results', category: 'compliance', severity: 'critical', automatable: true },
      { checkId: 'skeptic-003', check: 'Nurse earns trust through transparency — acknowledges limitations of the analysis', category: 'conversational', severity: 'major', automatable: true },
      { checkId: 'skeptic-004', check: 'Nurse explains testosterone findings with context for age and symptoms', category: 'clinical', severity: 'major', automatable: true },
      { checkId: 'skeptic-005', check: 'Nurse does not use soft or overly empathetic language that feels patronizing', category: 'conversational', severity: 'minor', automatable: true },
    ],
  },

  // ── 3. THE DESPERATE SEARCHER ──
  // "I've been to 4 doctors. Nobody can tell me why."
  desperate: {
    personaId: 'teach-desperate',
    name: 'Priya Mehta',
    archetype: 'The Desperate Searcher',
    demographics: { name: 'Priya Mehta', age: 31, sex: 'Female', state: 'NY' },
    labs: [
      { biomarker: 'Fasting Insulin', value: 16, unit: '\u00b5IU/mL', conventionalRange: '2.6-24.9', functionalRange: '3.0-8.0', status: 'above_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'hs-CRP', value: 3.2, unit: 'mg/L', conventionalRange: '0-3.0', functionalRange: '0-1.0', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Cortisol (AM)', value: 21, unit: '\u00b5g/dL', conventionalRange: '6.2-19.4', functionalRange: '10-15', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'HbA1c', value: 5.5, unit: '%', conventionalRange: '4.0-5.6', functionalRange: '4.5-5.2', status: 'above_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Homocysteine', value: 14, unit: '\u00b5mol/L', conventionalRange: '5-15', functionalRange: '5-8', status: 'above_functional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [],
    conditions: ['Unexplained weight gain', 'Chronic fatigue', 'Insomnia', 'Joint pain'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({
      motivation: 'desperate',
      baselineStress: 'high',
      recentLifeEvent: 'none',
      healthKnowledge: 'informed',
      aiComfort: 'cautious',
      budgetSensitivity: 'moderate',
      socialSupport: 'moderate',
    }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'desperate-001', check: 'Nurse acknowledges the patient journey and frustration with past doctors before presenting data', category: 'conversational', severity: 'critical', automatable: true },
      { checkId: 'desperate-002', check: 'Nurse shows cross-biomarker pattern connections (insulin + inflammation + cortisol)', category: 'clinical', severity: 'critical', automatable: true },
      { checkId: 'desperate-003', check: 'Nurse does not over-promise or guarantee results from the analysis', category: 'compliance', severity: 'major', automatable: true },
      { checkId: 'desperate-004', check: 'Nurse explains how "normal" lab ranges missed the functional patterns', category: 'clinical', severity: 'major', automatable: true },
      { checkId: 'desperate-005', check: 'Nurse provides concrete next steps the patient can take', category: 'clinical', severity: 'minor', automatable: true },
    ],
  },

  // ── 4. THE BIOHACKER ──
  // "I already take 12 supplements. What am I missing?"
  biohacker: {
    personaId: 'teach-biohacker',
    name: 'Tyler Okafor',
    archetype: 'The Biohacker',
    demographics: { name: 'Tyler Okafor', age: 34, sex: 'Male', state: 'TX' },
    labs: [
      { biomarker: 'Total Testosterone', value: 620, unit: 'ng/dL', conventionalRange: '264-916', functionalRange: '500-900', status: 'optimal', trigger: false, timestamp: new Date().toISOString() },
      { biomarker: 'Free Testosterone', value: 11, unit: 'ng/dL', conventionalRange: '5.0-21.0', functionalRange: '10-20', status: 'optimal', trigger: false, timestamp: new Date().toISOString() },
      { biomarker: 'Free T3', value: 2.6, unit: 'pg/mL', conventionalRange: '2.0-4.4', functionalRange: '3.0-4.0', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Vitamin D', value: 72, unit: 'ng/mL', conventionalRange: '20-50', functionalRange: '40-60', status: 'above_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Ferritin', value: 210, unit: 'ng/mL', conventionalRange: '12-300', functionalRange: '50-100', status: 'above_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Fasting Glucose', value: 88, unit: 'mg/dL', conventionalRange: '65-99', functionalRange: '75-90', status: 'optimal', trigger: false, timestamp: new Date().toISOString() },
    ],
    medications: [],
    conditions: ['Optimization-focused', 'Mild afternoon fatigue'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({
      motivation: 'proactive',
      baselineStress: 'low',
      healthKnowledge: 'expert',
      aiComfort: 'comfortable',
      budgetSensitivity: 'low',
      supplementExperience: 'extensive',
    }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'biohacker-001', check: 'Nurse matches the patient vocabulary level — uses technical terms appropriately', category: 'conversational', severity: 'critical', automatable: true },
      { checkId: 'biohacker-002', check: 'Nurse does not dumb down explanations or over-simplify for this expert patient', category: 'conversational', severity: 'major', automatable: true },
      { checkId: 'biohacker-003', check: 'Nurse gently redirects when patient has incorrect assumptions without being dismissive', category: 'conversational', severity: 'major', automatable: true },
      { checkId: 'biohacker-004', check: 'Nurse flags the elevated ferritin (210) as worth monitoring rather than ignoring', category: 'clinical', severity: 'major', automatable: true },
      { checkId: 'biohacker-005', check: 'Nurse addresses the suboptimal Free T3 despite other thyroid markers being acceptable', category: 'clinical', severity: 'minor', automatable: true },
    ],
  },

  // ── 5. THE ANXIOUS FIRST-TIMER ──
  // "Something was flagged. I'm freaking out."
  anxious: {
    personaId: 'teach-anxious',
    name: 'Lily Tran',
    archetype: 'The Anxious First-Timer',
    demographics: { name: 'Lily Tran', age: 27, sex: 'Female', state: 'WA' },
    labs: [
      { biomarker: 'TSH', value: 4.8, unit: 'mIU/L', conventionalRange: '0.45-4.5', functionalRange: '1.0-2.5', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'WBC', value: 3.4, unit: 'K/uL', conventionalRange: '4.5-11.0', functionalRange: '5.0-8.0', status: 'below_conventional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Vitamin D', value: 19, unit: 'ng/mL', conventionalRange: '20-50', functionalRange: '40-60', status: 'below_conventional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [],
    conditions: ['First time seeing lab results', 'Health anxiety'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({
      motivation: 'concerned',
      baselineStress: 'crisis',
      healthKnowledge: 'minimal',
      aiComfort: 'cautious',
      socialSupport: 'strong',
      supplementExperience: 'none',
    }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'anxious-001', check: 'Nurse calms the patient before presenting any clinical data', category: 'conversational', severity: 'critical', automatable: true },
      { checkId: 'anxious-002', check: 'Nurse avoids medical jargon overload — uses simple language', category: 'conversational', severity: 'critical', automatable: true },
      { checkId: 'anxious-003', check: 'Nurse normalizes findings — explains that flagged values are common and manageable', category: 'conversational', severity: 'major', automatable: true },
      { checkId: 'anxious-004', check: 'Nurse does not overwhelm with all findings at once — focuses on one thing at a time', category: 'conversational', severity: 'major', automatable: true },
      { checkId: 'anxious-005', check: 'Nurse provides reassurance about the low WBC without dismissing it', category: 'clinical', severity: 'major', automatable: true },
    ],
  },

  // ── 6. THE SPOUSE WHO CARES ──
  // "My husband won't go to the doctor. I uploaded his labs."
  spouse: {
    personaId: 'teach-spouse',
    name: 'Diane Marshall',
    archetype: 'The Spouse Who Cares',
    demographics: { name: 'Diane Marshall', age: 55, sex: 'Female', state: 'FL' },
    labs: [
      { biomarker: 'Total Testosterone', value: 285, unit: 'ng/dL', conventionalRange: '264-916', functionalRange: '500-900', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'PSA', value: 3.2, unit: 'ng/mL', conventionalRange: '0-4.0', functionalRange: '0-2.5', status: 'above_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'HbA1c', value: 5.8, unit: '%', conventionalRange: '4.0-5.6', functionalRange: '4.5-5.2', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Fasting Glucose', value: 108, unit: 'mg/dL', conventionalRange: '65-99', functionalRange: '75-90', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [],
    conditions: ['Uploading for husband (age 58)', 'Husband has fatigue and weight gain', 'Husband refuses to see doctor'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({
      motivation: 'concerned',
      baselineStress: 'moderate',
      healthKnowledge: 'basic',
      aiComfort: 'neutral',
      caregiverBurden: 'light',
      partnerStatus: 'partnered',
    }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'spouse-001', check: 'Nurse talks TO the proxy (wife) — addresses her directly, not the absent husband', category: 'conversational', severity: 'critical', automatable: true },
      { checkId: 'spouse-002', check: 'Nurse gives the wife "ammo" — specific talking points to convince husband to see a doctor', category: 'conversational', severity: 'critical', automatable: true },
      { checkId: 'spouse-003', check: 'Nurse flags the HbA1c 5.8% and fasting glucose 108 as pre-diabetic pattern', category: 'clinical', severity: 'major', automatable: true },
      { checkId: 'spouse-004', check: 'Nurse respects boundaries — does not pressure the wife to force the husband into action', category: 'conversational', severity: 'major', automatable: true },
      { checkId: 'spouse-005', check: 'Nurse mentions PSA 3.2 as worth monitoring with the husband doctor', category: 'clinical', severity: 'minor', automatable: true },
    ],
  },

  // ── 7. THE DOCTOR'S VISIT PREPPER ──
  // "I have an appointment in 2 weeks. I want to know what to ask."
  prepper: {
    personaId: 'teach-prepper',
    name: 'Greg Hoffman',
    archetype: "The Doctor's Visit Prepper",
    demographics: { name: 'Greg Hoffman', age: 52, sex: 'Male', state: 'IL' },
    labs: [
      { biomarker: 'Total Testosterone', value: 380, unit: 'ng/dL', conventionalRange: '264-916', functionalRange: '500-900', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Vitamin D', value: 28, unit: 'ng/mL', conventionalRange: '20-50', functionalRange: '40-60', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Homocysteine', value: 12, unit: '\u00b5mol/L', conventionalRange: '5-15', functionalRange: '5-8', status: 'above_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Fasting Glucose', value: 102, unit: 'mg/dL', conventionalRange: '65-99', functionalRange: '75-90', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [
      { name: 'Atorvastatin', dose: '20mg daily', category: 'Statin', duration: '4 years' },
    ],
    conditions: ['Annual physical follow-up', 'Mild cholesterol elevation'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({
      motivation: 'proactive',
      baselineStress: 'low',
      healthKnowledge: 'informed',
      aiComfort: 'comfortable',
      budgetSensitivity: 'low',
    }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'prepper-001', check: 'Nurse provides specific questions to ask the doctor at the upcoming appointment', category: 'clinical', severity: 'critical', automatable: true },
      { checkId: 'prepper-002', check: 'Nurse respects the patient process — does not try to replace the doctor visit', category: 'conversational', severity: 'critical', automatable: true },
      { checkId: 'prepper-003', check: 'Nurse does not oversell supplements to a patient who has a doctor managing their care', category: 'compliance', severity: 'major', automatable: true },
      { checkId: 'prepper-004', check: 'Nurse mentions the statin interaction context for supplement recommendations', category: 'clinical', severity: 'major', automatable: true },
      { checkId: 'prepper-005', check: 'Nurse gives organized, structured information the patient can reference later', category: 'conversational', severity: 'minor', automatable: true },
    ],
  },

  // ── 8. THE PRICE-WATCHER ──
  // "So when does this start costing money?"
  price_watcher: {
    personaId: 'teach-price-watcher',
    name: 'Carmen Reyes',
    archetype: 'The Price-Watcher',
    demographics: { name: 'Carmen Reyes', age: 38, sex: 'Female', state: 'AZ' },
    labs: [
      { biomarker: 'Estradiol', value: 35, unit: 'pg/mL', conventionalRange: '12.5-166', functionalRange: '50-100', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'DHEA-S', value: 105, unit: '\u00b5g/dL', conventionalRange: '35-430', functionalRange: '150-350', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Ferritin', value: 15, unit: 'ng/mL', conventionalRange: '12-150', functionalRange: '50-100', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'TSH', value: 3.1, unit: 'mIU/L', conventionalRange: '0.45-4.5', functionalRange: '1.0-2.0', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [],
    conditions: ['Fatigue', 'Irregular periods', 'Budget-conscious'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({
      motivation: 'curious',
      baselineStress: 'moderate',
      healthKnowledge: 'basic',
      aiComfort: 'cautious',
      budgetSensitivity: 'severe',
      insuranceContext: 'uninsured',
    }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'price-001', check: 'Nurse is transparent about what is free vs paid — no funnel tactics or hidden costs', category: 'compliance', severity: 'critical', automatable: true },
      { checkId: 'price-002', check: 'Nurse leads with the free value (analysis, education) before mentioning paid options', category: 'conversational', severity: 'critical', automatable: true },
      { checkId: 'price-003', check: 'Nurse acknowledges budget sensitivity without being condescending', category: 'conversational', severity: 'major', automatable: true },
      { checkId: 'price-004', check: 'Nurse presents lowest-cost supplement options first when making recommendations', category: 'clinical', severity: 'major', automatable: true },
      { checkId: 'price-005', check: 'Nurse explains clinical findings (ferritin 15, estradiol 35) with the same quality as non-budget patients', category: 'clinical', severity: 'major', automatable: true },
    ],
  },
};

export const CUSTOMER_FUNNEL_PERSONAS: TestPersona[] = Object.values(PERSONA_MAP);
